/**
 * Represents a data use report line, based on the protobuf definition in terminal-rpc.proto:
 *
 *  message DataUseReport {
 *    int64 id = 1;
 *    uint64 total_rx_bytes = 2;
 *    uint64 total_tx_bytes = 3;
 *    uint64 at_rx_bytes = 4;
 *    uint64 at_tx_bytes = 5;
 *    uint64 mobile_rx_bytes = 6;
 *    uint64 mobile_tx_bytes = 7;
 *    uint64 accumulation_time = 8;
 *    uint64 report_time = 9;
 *  }
 *
 */
import {numOrNull, nvl, requireNum} from "@/modules/utils";

export class DataUseReport {
    public constructor(
        private _id: number,
        private _totalRxBytes: number | null,
        private _totalTxBytes: number | null,
        private _atRxBytes: number | null,
        private _atTxBytes: number | null,
        private _mobileRxBytes: number | null,
        private _mobileTxBytes: number | null,
        private _accumulationTime: number,
        private _reportTime: number,
    ){}

    static fromJson(json: any): DataUseReport {
        return new DataUseReport(
            requireNum(json.id),
            numOrNull(json.total_rx_bytes),
            numOrNull(json.total_tx_bytes),
            numOrNull(json.at_rx_bytes),
            numOrNull(json.at_tx_bytes),
            numOrNull(json.mobile_rx_bytes),
            numOrNull(json.mobile_tx_bytes),
            requireNum(json.accumulation_time),
            requireNum(json.report_time),
        )
    }

    static fromJsonArray(jsonArray: any[]): DataUseReport[] {
        return jsonArray.map(DataUseReport.fromJson);
    }

    get id() {
        return this._id;
    }

    static kiloBytes(bytes: number | null): number | null {
        return bytes !== null ? Math.round(bytes/1024) : null;
    }

    /**
     * Convert milliseconds to a whole number of minutes
     * @param milliseconds
     */
    static minutes(milliseconds: number): number {
        return Math.round(milliseconds / 1000 / 60 );
    }

    get totalRxKb() {
        return DataUseReport.kiloBytes(this._totalRxBytes)
    }

    get totalTxKb() {
        return DataUseReport.kiloBytes(this._totalTxBytes);
    }

    get atRxKb() {
        return DataUseReport.kiloBytes(this._atRxBytes);
    }

    get atTxKb() {
        return DataUseReport.kiloBytes(this._atTxBytes)
    }

    get mobileRxKb() {
        return DataUseReport.kiloBytes(this._mobileRxBytes);
    }

    get mobileTxKb() {
        return DataUseReport.kiloBytes(this._mobileTxBytes);
    }

    get accumulationTimeMinutes() {
        return DataUseReport.minutes(this._accumulationTime);
    }

    get accumulationTimeHours() {
        return Math.round(this.accumulationTimeMinutes / 60);
    }

    get reportTime(): Date {
        return new Date(this._reportTime);
    }

    static nvlSum(a: number | null, b: number | null): number | null {
        if(a === null && b === null) return null;
        return nvl(a, 0) + nvl(b, 0);
    }

    plus(other: DataUseReport): DataUseReport {
        return new DataUseReport(
            0,
            DataUseReport.nvlSum(this._totalRxBytes, other._totalRxBytes),
            DataUseReport.nvlSum(this._totalTxBytes, other._totalTxBytes),
            DataUseReport.nvlSum(this._atRxBytes, other._atRxBytes),
            DataUseReport.nvlSum(this._atTxBytes, other._atTxBytes),
            DataUseReport.nvlSum(this._mobileRxBytes, other._mobileRxBytes),
            DataUseReport.nvlSum(this._mobileTxBytes, other._mobileTxBytes),
            this._accumulationTime + other._accumulationTime,
            Math.max(this._reportTime, other._reportTime), // report time should be the last time summed
        )
    }

    static empty(): DataUseReport {
        const nowUnix = (new Date()).getTime();
        return new DataUseReport(0, null, null, null, null, null, null, 0, nowUnix);
    }
}
